import {
  createTemplateFromSailingDay,
  getSailing,
  publishSailingDay,
} from '@go-client/lib/api/itinerary';
import {
  getPlan,
  populate as populatePlan,
  createEvent,
  updateEvent,
  exportPlan,
  deleteEvent,
  updateVenueData,
  createContent,
  updateContent,
  deleteContent,
} from '@go-client/lib/api/plan';
import { updateTags } from '@go-client/lib/api/property';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import DailyPlanner, { DailyPlannerInfo } from '@go-client/components/DailyPlanner';
import { BareEvent } from '@go-shared/types/event';
import { MenuBar } from '@go-client/components';
import Page from './Page';
import { VenueDisposition } from '@go-shared/types/plan';
import { FragmentBlockSync } from '@go-client/components/TemplateLinkForm/TemplateLinkForm';
import { Popup } from '@spark-labs/avocado-ui';
import { BareSimpleSailing, BareSimpleSailingDay } from '@go-shared/types/sailing';
import TemplateForm, { TemplateFormDataBase } from '@go-client/components/TemplateForm';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import PageWrapper from './PageWrapper';
import { attemptCalcAdjacentSailingDay } from '@go-client/lib/sailing';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import {
  resolveFeedPath,
  resolvePlannerPath,
  resolveTemplateFeedPath,
  resolveTemplatePlannerPath,
} from '@go-client/routes';
import { BareContent } from '@go-shared/types/content';
import { makeSailingDayBundle } from '@go-shared/lib/sailing';
import { bare, bareSailingDay, populate, populateDay } from '@go-shared/lib/itinerary';
import { getPropertyEventTemplates } from '@go-client/lib/api/eventTemplate';
import { getVenueTemplatesByPropertyId } from '@go-client/lib/api/venueTemplate';
import { getPropertyContentTemplates } from '@go-client/lib/api/contentTemplate';
import { getStaffTemplatesByPropertyId } from '@go-client/lib/api/staffTemplate';
import { populateEventTemplate } from '@go-shared/lib/eventTemplate';
import { populateVenueTemplate } from '@go-shared/lib/venue';
import { populateStaffTemplate } from '@go-shared/lib/staff';
import { getTranslations, getTranslationsByRefers } from '@go-client/lib/api/translation';

interface CreateTemplateForm {
  sailing: BareSimpleSailing;
  sailingDay: BareSimpleSailingDay;
  templateData: TemplateFormDataBase;
}

export interface SailingDailyPlannerParams {
  sailing: string;
  sailingDay: string;
}

type SailingDailyPlannerPageProps = RouteComponentProps<SailingDailyPlannerParams>;

export default function SailingDailyPlanner(props: SailingDailyPlannerPageProps) {
  const [createTemplateForm, setCreateTemplateForm] = useState<CreateTemplateForm | undefined>(
    undefined
  );
  const [showCreateTemplateForm, setShowCreateTemplateForm] = useState(false);
  const [plannerState, setPlannerState] = useState<DailyPlannerInfo | undefined>(undefined);
  const [viewPublished, setViewPublished] = useState(false);

  const plannerInfo =
    !plannerState || !viewPublished
      ? plannerState
      : {
          ...plannerState,
          plan: {
            ...plannerState.plan,
            events: plannerState.plan.published?.events ?? [],
            venueData: plannerState.plan.published?.venueData ?? [],
            content: plannerState.plan.published?.content ?? [],
            published: {
              events: plannerState.plan.events,
              venueData: plannerState.plan.venueData,
              content: plannerState.plan.content,
            },
          },
        };
  const { sailing: sailingId, sailingDay: sailingDayId } = props.match.params;

  const { activeProperty: property } = useActiveProperty();

  useEffect(() => {
    loadSailing({ includeLibraries: true, includeTranslations: true }).catch(console.error);
  }, [sailingId, sailingDayId]);

  async function loadSailing(opt?: { includeLibraries?: boolean; includeTranslations?: boolean }) {
    const bareSailing = await getSailing(sailingId);
    const bareSailingDayIdx = bareSailing.days.findIndex(d => d._id === sailingDayId);

    if (bareSailingDayIdx < 0) {
      throw new Error(`Could not find sailing day ${sailingDayId}`);
    }

    const bareSailingDay = bareSailing.days[bareSailingDayIdx];
    const barePlan = await getPlan(bareSailingDay.plan);
    const plan = populatePlan(barePlan, property);

    let eventTmpls = plannerInfo?.eventTmpls ?? [];
    let venueTmpls = plannerInfo?.venueTmpls ?? [];
    let contentTmpls = plannerInfo?.contentTmpls ?? [];
    let staffTmpls = plannerInfo?.staffTmpls ?? [];
    let eventTmplTransls = plannerInfo?.eventTmplTransls ?? [];
    let contentTmplTransls = plannerInfo?.contentTmplTransls ?? [];
    let venueTmplTransls = plannerInfo?.venueTmplTransls ?? [];
    let commonTextTransls = plannerInfo?.commonTextTransls ?? [];

    if (opt?.includeLibraries) {
      const [bareEventTemplates, bareVenueTemplates, bareContentTemplates, bareStaffTemplates] =
        await Promise.all([
          getPropertyEventTemplates(plan.property._id),
          getVenueTemplatesByPropertyId(plan.property._id),
          getPropertyContentTemplates(plan.property._id),
          getStaffTemplatesByPropertyId(plan.property._id),
        ]);

      eventTmpls = bareEventTemplates.map(et => populateEventTemplate(et, plan.property.brand));
      venueTmpls = bareVenueTemplates.map(vt => populateVenueTemplate(vt, plan.property));
      contentTmpls = bareContentTemplates;
      staffTmpls = bareStaffTemplates.map(st => populateStaffTemplate(st, plan.property));
    }

    if (opt?.includeTranslations) {
      const eventTemplateIds = eventTmpls.map(et => et._id);
      const venueTemplateIds = venueTmpls.map(vt => vt._id);
      const contentTemplateIds = contentTmpls.map(ct => ct._id);
      const [
        eventTemplateTranslations,
        venueTemplateTranslations,
        contentTemplateTranslations,
        commonTextTranslations,
      ] = await Promise.all([
        getTranslationsByRefers('eventtemplate', eventTemplateIds),
        getTranslationsByRefers('venuetemplate', venueTemplateIds),
        getTranslationsByRefers('contenttemplate', contentTemplateIds),
        getTranslations(plan.property._id, 'commonText'),
      ]);

      eventTmplTransls = eventTemplateTranslations;
      venueTmplTransls = venueTemplateTranslations;
      contentTmplTransls = contentTemplateTranslations;
      commonTextTransls = commonTextTranslations;
    }

    // calculate link to previous day and to next day so that the daily planner
    // can render a chevron to go forward or backwards within the canvas
    const adjacentSailingBundles = await attemptCalcAdjacentSailingDay(
      bareSailing,
      bareSailingDayIdx
    );

    const nextSailingBundle = adjacentSailingBundles
      ? adjacentSailingBundles.nextSailingBundle
      : undefined;
    const prevSailingBundle = adjacentSailingBundles
      ? adjacentSailingBundles.prevSailingBundle
      : undefined;

    const nextSailingDayLink = nextSailingBundle
      ? resolvePlannerPath(
          nextSailingBundle.sailing.property,
          nextSailingBundle.sailing._id,
          nextSailingBundle.sailingDay._id
        )
      : undefined;

    const prevSailingDayLink = prevSailingBundle
      ? resolvePlannerPath(
          prevSailingBundle.sailing.property,
          prevSailingBundle.sailing._id,
          prevSailingBundle.sailingDay._id
        )
      : undefined;

    const pathToFeed = resolveFeedPath(plan.property._id, bareSailing._id, bareSailingDay._id);

    const dayBundle = makeSailingDayBundle(
      populate(bareSailing, property, [], []),
      populateDay(bareSailingDay, [])
    );

    setPlannerState({
      plan,
      dayBundle,
      pathToFeed,
      nextSailingDayLink,
      prevSailingDayLink,
      eventTmpls,
      venueTmpls,
      contentTmpls,
      staffTmpls,
      eventTmplTransls,
      contentTmplTransls,
      venueTmplTransls,
      commonTextTransls,
    });
  }

  async function handleOnGenerateSchedule() {
    if (!plannerInfo) {
      throw new Error(`Attempted to generate a schedule on an unresolved plan!`);
    }

    const result = await exportPlan(plannerInfo.plan._id);

    if (result.location) window.open(result.location, '_blank');
  }

  function handleOnGenerateReport() {
    props.history.push(`/reports/${sailingId}/${sailingDayId}`);
  }

  function handleOnCreateEvent(event: BareEvent, applyStaffToPlan?: boolean) {
    if (!plannerInfo) {
      throw new Error(`Attempted to create an event for an unresolved plan!`);
    }

    return createEvent(plannerInfo.plan._id, event, applyStaffToPlan);
  }

  function handleOnDeleteEvent(eventId: string) {
    if (!plannerInfo) {
      throw new Error(`Attempted to delete an event for an unresolved plan!`);
    }

    return deleteEvent(plannerInfo.plan._id, eventId);
  }

  function handleOnUpdateEvent(
    event: BareEvent,
    applyStaffToPlan?: boolean,
    projectUpdateKey?: string,
    transferToTargetDay?: string
  ) {
    if (!plannerInfo) {
      throw new Error(`Attempted to update an event for an unresolved plan!`);
    }

    return updateEvent(
      plannerInfo.plan._id,
      event,
      projectUpdateKey,
      applyStaffToPlan,
      transferToTargetDay
    );
  }

  function handleOnCreateContent(content: BareContent) {
    if (!plannerInfo) {
      throw new Error(`Attempted to delete an event for an unresolved plan!`);
    }

    return createContent(plannerInfo.plan._id, content);
  }

  function handleOnUpdateContent(content: BareContent) {
    if (!plannerInfo) {
      throw new Error(`Attempted to update a content for an unresolved plan!`);
    }

    return updateContent(plannerInfo.plan._id, content);
  }

  function handleOnDeleteContent(contentId: string) {
    if (!plannerInfo) {
      throw new Error(`Attempted to delete a content for an unresolved plan!`);
    }

    return deleteContent(plannerInfo.plan._id, contentId);
  }

  function handleOnUpdateVenueData(venueData: VenueDisposition) {
    if (!plannerInfo) {
      throw new Error(`Attempted to update a venue data for an unresolved plan!`);
    }

    return updateVenueData(plannerInfo.plan._id, venueData);
  }

  function handleOnPublishSailingDay() {
    if (!plannerInfo || !plannerInfo.dayBundle) {
      throw new Error(`Attempted to publish an unresolved plan!`);
    }

    return publishSailingDay(plannerInfo.dayBundle.sailing._id, plannerInfo.dayBundle.day._id);
  }

  function handleOnTemplateNameClick(blockSync: FragmentBlockSync) {
    if (!plannerInfo) return;

    props.history.push(
      blockSync.type === 'feed'
        ? resolveTemplateFeedPath(property._id, blockSync.templateId)
        : resolveTemplatePlannerPath(property._id, blockSync.templateId)
    );
  }

  function handleOnSailingNameClick(sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) {
    if (!plannerInfo) return;

    props.history.push(resolvePlannerPath(property._id, sailing._id, sailingDay._id));
  }

  function handleCreateTemplateFormClose() {
    setShowCreateTemplateForm(false);
  }

  async function handleOnCreateTemplateFromSailingDay(templateData: TemplateFormDataBase) {
    if (!createTemplateForm) return;

    await createTemplateFromSailingDay(
      createTemplateForm.sailing._id,
      createTemplateForm.sailingDay._id,
      {
        name: templateData.name,
        tags: templateData.tags.map(t => t._id),
        ...(templateData.dayTheme && { dayTheme: templateData.dayTheme }),
      }
    );

    handleCreateTemplateFormClose();
  }

  function handleOnCreateTemplateClick(
    sailing: BareSimpleSailing,
    sailingDay: BareSimpleSailingDay
  ) {
    setCreateTemplateForm({
      sailing,
      sailingDay,
      templateData: {
        name: '',
        placeholder: `Copy of ${sailing.name} (Day ${sailingDay.name})`,
        tags: [],
      },
    });
    setShowCreateTemplateForm(true);
  }

  let gridTitle = '';
  const dayBundle = plannerInfo?.dayBundle;

  if (dayBundle) {
    switch (property.type) {
      case 'sea':
        let seaDNF = getPropertyConfiguration(property.type, 'dayNameFormatter');
        gridTitle = seaDNF(bare(dayBundle.sailing), bareSailingDay(dayBundle.day));
        break;
      case 'shore':
        let shoreDNF = getPropertyConfiguration(property.type, 'dayNameFormatter');
        gridTitle = shoreDNF(bare(dayBundle.sailing), bareSailingDay(dayBundle.day));
        break;
    }
  }

  return (
    <PageWrapper {...props}>
      <Page className="DailyPlanner absolute flex items-stretch overflow-hidden">
        <MenuBar {...props} property={property} />
        <DailyPlanner
          variant="navy"
          gridTitle={gridTitle}
          plannerInfo={plannerInfo}
          onGenerateReport={handleOnGenerateReport}
          onGenerateSchedule={handleOnGenerateSchedule}
          onCreateEvent={handleOnCreateEvent}
          onUpdateEvent={handleOnUpdateEvent}
          onDeleteEvent={handleOnDeleteEvent}
          onUpdateVenueData={handleOnUpdateVenueData}
          onReloadPlan={loadSailing}
          onTemplateNameClick={handleOnTemplateNameClick}
          onCreateTemplateClick={handleOnCreateTemplateClick}
          onCreateContent={handleOnCreateContent}
          onUpdateContent={handleOnUpdateContent}
          onDeleteContent={handleOnDeleteContent}
          onPublishPlan={handleOnPublishSailingDay}
          onSailingDayNameClick={handleOnSailingNameClick}
          viewPublished={viewPublished}
          toggleViewPublished={() => setViewPublished(!viewPublished)}
        />
        {plannerInfo && showCreateTemplateForm && (
          <Popup title="Create a Template from Day" onClose={handleCreateTemplateFormClose}>
            {createTemplateForm && (
              <>
                <hr />
                <div className="p-4" style={{ width: '500px' }}>
                  <div className="p-3 mb-3 bg-eggshell shadow rounded border">
                    Creating a template from:{' '}
                    <span className="font-bold">
                      {createTemplateForm.sailing.name} (Day {createTemplateForm.sailingDay.name})
                    </span>
                  </div>
                  <TemplateForm
                    submitButtonTitle="Create Template"
                    initialForm={createTemplateForm.templateData}
                    propertyTags={plannerInfo.plan.property.tags}
                    onSubmit={handleOnCreateTemplateFromSailingDay}
                    onUpdatePropertyTags={async propertyTags => {
                      await updateTags(plannerInfo.plan.property._id, propertyTags);
                      await loadSailing();
                    }}
                  />
                </div>
              </>
            )}
          </Popup>
        )}
      </Page>
    </PageWrapper>
  );
}
